import React from "react";
import { motion } from "framer-motion";
import { AlertTriangle, CheckCircle2, TrendingUp } from "lucide-react";
import { useApp } from "@/context/AppStore";
import { KpiStat, Panel, StatusPill, AnimatedNumber } from "@/components/common/Widgets";
import { IntelligenceGauge } from "@/components/IntelligenceGauge";
import { WhyCard } from "@/components/WhyCard";
import { fmtNum, fmtINR, fmtLakh, signNum, signPct } from "@/lib/format";

export default function CommandCenter() {
  const { snapshot } = useApp();
  if (!snapshot) return <Loading />;

  const ct = snapshot.coal_today;
  const eco = snapshot.economics;
  const kpi = {
    inventory: snapshot.coal.inventory_mt,
    coalCost: eco.coal_cost_today,
    eff: snapshot.boiler.efficiency_pct,
    steam: snapshot.steam.generated_tpd,
    product: snapshot.production.total_mt,
    leak: eco.ebitda_leakage,
  };

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-4 animate-fade-up" data-testid="command-center">
      {/* header */}
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <div className="eyebrow">Executive Command Center</div>
          <h1 className="font-display font-extrabold text-2xl uppercase tracking-tight text-ink">Today at Akry Organics</h1>
        </div>
        <StatusPill status={ct.status} label={ct.status === "nominal" ? "On Target" : "Attention Required"} />
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-strip">
        <KpiStat testid="kpi-inventory" label="Coal Inventory" value={kpi.inventory} unit="MT" color="#8BA1C0" />
        <KpiStat testid="kpi-coal-cost" label="Coal Cost Today" value={kpi.coalCost / 1e5} unit="L ₹" decimals={1} color="#fab20b" />
        <KpiStat testid="kpi-boiler-eff" label="Boiler Efficiency" value={kpi.eff} unit="%" decimals={1}
          color={kpi.eff >= 84 ? "#33b76d" : kpi.eff >= 80 ? "#fab20b" : "#E63946"} />
        <KpiStat testid="kpi-steam" label="Steam Generated" value={kpi.steam} unit="MT" color="#33b76d" />
        <KpiStat testid="kpi-product" label="Product Output" value={kpi.product} unit="MT" decimals={1} color="#33b76d" />
        <KpiStat testid="kpi-leakage" label="EBITDA Leakage" value={kpi.leak / 1e5} unit="L ₹" decimals={2} color="#E63946" />
      </div>

      {/* hero: gauge + why */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3">
          <Panel eyebrow="Coal-to-Product Intelligence" title="The Heartbeat of the Plant" testid="gauge-panel"
            right={<StatusPill status={ct.status} />}>
            <div className="pt-2 pb-1">
              <IntelligenceGauge expected={ct.predicted_mt} actual={ct.consumed_mt} variancePct={ct.variance_pct} status={ct.status} />
            </div>
          </Panel>
        </div>
        <div className="lg:col-span-2">
          <WhyCard why={snapshot.why} />
        </div>
      </div>

      {/* today + alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Panel eyebrow="Daily Snapshot" title="Today" testid="today-panel">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <TodayStat label="Production" value={fmtNum(snapshot.production.total_mt, 1)} unit="MT" />
              <TodayStat label="Coal Consumed" value={fmtNum(ct.consumed_mt, 1)} unit="MT" color="#fab20b" />
              <TodayStat label="Predicted Coal" value={fmtNum(ct.predicted_mt, 1)} unit="MT" />
              <TodayStat label="Variance" value={signNum(ct.variance_mt, 1)} unit="MT" color="#E63946" sub={signPct(ct.variance_pct)} />
              <TodayStat label="Potential Loss" value={fmtLakh(eco.potential_loss)} color="#E63946" />
              <TodayStat label="Cost / Tonne" value={fmtINR(eco.cost_per_tonne_product)} />
            </div>
          </Panel>
        </div>
        <Panel eyebrow="Alert Engine" title="Active Alerts" testid="alerts-panel">
          <div className="space-y-2">
            {(snapshot.alerts || []).map((a, i) => {
              const c = a.severity === "critical" ? "#E63946" : a.severity === "warning" ? "#fab20b" : "#33b76d";
              const Icon = a.severity === "nominal" ? CheckCircle2 : AlertTriangle;
              return (
                <motion.div key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                  className="panel-2 px-3 py-2.5 border-l-2 flex items-start gap-2.5" style={{ borderLeftColor: c }}>
                  <Icon size={15} style={{ color: c }} className="mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[0.8rem] font-medium text-ink">{a.title}</div>
                    <div className="text-[0.68rem] text-ink-soft font-mono mt-0.5">{a.detail}</div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function TodayStat({ label, value, unit, color = "#FFFFFF", sub }) {
  return (
    <div className="panel-2 px-3.5 py-3">
      <div className="eyebrow">{label}</div>
      <div className="flex items-baseline gap-1 mt-1">
        <span className="text-lg font-bold tnum" style={{ color }}>{value}</span>
        {unit && <span className="text-[0.7rem] text-ink-soft font-mono">{unit}</span>}
      </div>
      {sub && <div className="text-[0.68rem] font-mono mt-0.5" style={{ color }}>{sub}</div>}
    </div>
  );
}

function Loading() {
  return <div className="p-10 text-center text-ink-soft font-mono text-sm" data-testid="loading">Connecting to plant telemetry…</div>;
}
